// src/pages/BlogDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft, Clock, Share2, MessageCircle } from 'react-feather';

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetch(`https://dawamaxma.dawamtaani.co.ke/api/blogs/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setBlog(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching blog:', error);
        setLoading(false);
      });
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Blog post not found</h2>
        <button
          onClick={() => navigate('/blogs')}
          className="flex items-center text-primary-600 font-semibold hover:underline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blogs
        </button>
      </div>
    );
  }

  const publishDate = new Date(blog.created_at || Date.now()).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Title */}
      <div className="bg-gray-50 py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button
              onClick={() => navigate('/blogs')}
              className="group flex items-center text-primary-600 font-bold mb-8 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
              Back to Journal
            </button>

            <span className="inline-block bg-primary-100 text-primary-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-6">
              Health Tips
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-neutral-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            <p className="text-xl text-neutral-600 mb-8 leading-relaxed italic">
              {blog.subheading}
            </p>

            <div className="flex flex-wrap items-center text-sm text-neutral-500 gap-6 py-6 border-y border-neutral-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <User className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-bold text-neutral-900">Admin</p>
                  <p className="text-xs text-neutral-400">Medical Expert</p>
                </div>
              </div>
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-primary-400" />
                <span>{publishDate}</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-primary-400" />
                <span>5 min read</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="container mx-auto px-4 -mt-12 md:-mt-16 mb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl"
        >
          <img
            src={`https://dawamaxma.dawamtaani.co.ke/${blog.image}`}
            alt={blog.title}
            className="w-full h-[400px] md:h-[600px] object-cover"
          />
        </motion.div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-24">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg prose-primary max-w-none"
          >
            {blog.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index} className="text-neutral-700 text-lg leading-relaxed mb-6">
                  {paragraph}
                </p>
              )
            ))}
          </motion.div>

          {/* Social Share & Footer */}
          <div className="mt-16 pt-8 border-t border-neutral-100 flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-bold text-neutral-400 uppercase tracking-widest">Share this:</span>
              <div className="flex space-x-2">
                {[Share2, MessageCircle].map((Icon, i) => (
                  <button key={i} className="p-2 bg-neutral-50 rounded-full text-neutral-400 hover:bg-primary-50 hover:text-primary-600 transition-all">
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/blogs"
              className="inline-flex items-center bg-primary-600 text-white px-8 py-3 rounded-full font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-primary-200"
            >
              Discover More Stories
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
